/**
 * Salt prefix of the media key root. It keeps the cipher key independent of
 * the access proof (`andor/room-access/v1/`), which the server sees — so the
 * server can never derive a key that decrypts traffic.
 */
const ROOT_SALT_PREFIX = 'andor/room-media/v1/';
/** HKDF `info` prefix; the epoch number follows. */
const EPOCH_INFO_PREFIX = 'andor/room-media/v1/epoch/';

/** Matches the proof's and the cipher's own key-derivation cost. */
export const ROOM_MEDIA_ROOT_ITERATIONS = 600_000;

// The root derivation is deliberately slow (~0.2–1 s) and needed on every join
// and rotation: memoise per tab. In memory only, and non-extractable — the
// password itself is already saved in RoomPasswordStore.
const roots = new Map<string, Promise<CryptoKey>>();

/**
 * The string an encrypted room's members hand to the ODIN cipher for one media
 * key epoch (`RoomMediaKeySchema`), instead of the raw password:
 * `HKDF-SHA256(root, salt = empty, info = "andor/room-media/v1/epoch/" + epoch,
 * 256 bits)` as unpadded base64url, over the per-room root
 * `PBKDF2-HMAC-SHA256(password, "andor/room-media/v1/" + roomId, 600k)`.
 * One epoch's key reveals neither the password nor any other epoch's key, so a
 * recorder handed the key of its epoch hears nothing before or after it.
 * `roomId` must be the canonical id (the join response's), `password` the exact
 * stored string. `iterations` is injectable for fast unit tests only.
 */
export async function deriveRoomMediaKey(
  password: string,
  roomId: string,
  epoch: number,
  iterations = ROOM_MEDIA_ROOT_ITERATIONS
): Promise<string> {
  const root = await roomMediaRoot(password, roomId, iterations);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(),
      info: new TextEncoder().encode(EPOCH_INFO_PREFIX + epoch),
    },
    root,
    256
  );
  return toBase64Url(new Uint8Array(bits));
}

/**
 * Start (or reuse) the slow root derivation of `roomId`, e.g. alongside the
 * access proof while a join request is in flight.
 */
export function roomMediaRoot(
  password: string,
  roomId: string,
  iterations = ROOM_MEDIA_ROOT_ITERATIONS
): Promise<CryptoKey> {
  const key = JSON.stringify([roomId, password, iterations]);
  let root = roots.get(key);
  if (!root) {
    root = deriveRoot(password, roomId, iterations);
    roots.set(key, root);
    root.catch(() => roots.delete(key));
  }
  return root;
}

async function deriveRoot(
  password: string,
  roomId: string,
  iterations: number
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const material = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  // WebCrypto cannot derive an HKDF key from PBKDF2 directly (HKDF has no key
  // length), so derive the bits and import them, non-extractable.
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: encoder.encode(ROOT_SALT_PREFIX + roomId),
      iterations,
    },
    material,
    256
  );
  return crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveBits']);
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
